import { saveAs } from 'file-saver'
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { useDispatch } from 'react-redux'

import { Card } from '@/components/Elements'
import { Checkbox, FileInput, Input, Select } from '@/components/Form'
import { useAdminFunction } from '@/features/adminFunction/useAdminFunction'
import { OfferDocumentTypes } from '@/features/offers/constants'
import useUser from '@/features/users/useUser'
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'
import { isNullish } from '@/utils/helper'
import { Box, Button, Flex } from '@chakra-ui/react'
import { useEffect } from 'react'
import { toast } from 'react-toastify'
import { ConfigType } from '../../config.enum'
import useConfig from '../../useConfig'
import { OfferType } from '@eapproval/offer-shared'
import { camelToWords } from '@/utils/stringHelper'

const SignCommercialProposalCard = () => {
  const methods = useForm({
    defaultValues: {
      fileName: 'signed_offer_commercial_proposal',
      signUsers: [
        {
          userId: '',
          isDelegated: false,
        },
      ],
    },
  })
  const dispatch = useDispatch()
  const { useUserOptions } = useUser()
  const { useFetchConfigByType } = useConfig()
  const { useSignCommercialProposal } = useAdminFunction()
  const { fields: signUsers, append: appendSignUser } = useFieldArray({ name: 'signUsers', control: methods.control })
  const { data: signatureLabelConfig } = useFetchConfigByType(ConfigType.SIGNATURE_LABEL)

  const shouldCombineTermsAndCondition = useWatch({ name: 'shouldCombineTermsAndCondition', control: methods.control })
  //Translated Variables

  const { data: userOptions } = useUserOptions({ labelKey: 'fullName', valueKey: '_id' })
  const signCommercialProposal = useSignCommercialProposal()

  //TODO: revamp to fetch one config only
  useEffect(() => {
    if (signatureLabelConfig) {
      methods.setValue('signLabel', signatureLabelConfig.mainValue ?? '')
    }
  }, [signatureLabelConfig, methods])

  const onSubmit = async ({
    fileName,
    commercialProposal,
    signLabel,
    signUsers,
    subSystemType,
    shouldCombineTermsAndCondition,
  }) => {
    if (!fileName) {
      toast.error('Missing File Name')
      return
    }
    if (!commercialProposal) {
      toast.error('Missing Uploaded File')
      return
    }
    if (isNullish(signLabel) || signLabel === '') {
      toast.error('Missing Sign Label')
      return
    }

    if (signUsers.some((user) => user.userId === '')) {
      toast.error('Missing Sign User Name')
      return
    }

    try {
      dispatch(showLoadingOverlay())
      const payload = {
        subSystemType,
        shouldCombineTermsAndCondition,
        commercialProposalId: commercialProposal?._id,
        signLabel: signLabel,
        signUsers: signUsers,
      }

      const response = await signCommercialProposal.mutateAsync(payload)

      const blob = new Blob([response])

      saveAs(blob, `${fileName}.pdf`)
    } catch (err) {
      let errorMessage = err?.response?.data?.message || err?.message || 'Failed to sign commercial proposal'

      if (err?.response?.data instanceof Blob) {
        const blobText = await err.response.data.text()

        try {
          const parsedError = JSON.parse(blobText)
          errorMessage = parsedError?.message || errorMessage
        } catch (parseError) {
          if (blobText) {
            errorMessage = blobText
          }
        }
      }

      toast.error(errorMessage)
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  const handleAddSignUser = () => {
    appendSignUser({
      userId: '',
      isDelegated: false,
    })
  }
  return (
    <Card
      title="Sign Commercial Proposal"
      rightElement={
        <Button colorScheme={'teal'} onClick={handleAddSignUser}>
          Add Sign User
        </Button>
      }
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Box p={5}>
            <Flex direction="column" gap="3">
              <FileInput
                label="Commercial Proposal"
                name="commercialProposal"
                fileType={OfferDocumentTypes.DOCUMENTS_EXTERNAL_COMMERCIAL_PROPOSAL}
                canUpload
                isRequired
                isRow
              />
              <Input name="signLabel" label="Sign Label" isRow />
              <Input name="fileName" label="File Name" isRow />
              <Checkbox name="shouldCombineTermsAndCondition" label="Should Combine T & C" isRow />

              {shouldCombineTermsAndCondition && (
                <Select
                  name="subSystemType"
                  label="Sub System Type"
                  isRow
                  options={Object.values(OfferType).map((type) => {
                    return {
                      label: camelToWords(type),
                      value: type,
                    }
                  })}
                />
              )}
              {signUsers.map((signUser, index) => (
                <Flex key={signUser.id} gap="3" alignItems={'center'}>
                  <Select
                    name={`signUsers.${index}.userId`}
                    label={`Sign User ${index + 1}`}
                    isRow
                    options={userOptions}
                  />
                  <Checkbox name={`signUsers.${index}.isDelegated`} label="Is Delegated" type="checkbox" isRow />
                </Flex>
              ))}
            </Flex>
            <Button mt="6" colorScheme="blue" type="submit">
              Sign
            </Button>
          </Box>
        </form>
      </FormProvider>
    </Card>
  )
}

export default SignCommercialProposalCard
